// Shared tile-processing pipeline used by both:
//   - DispatchBoard.js (production breakdown page)
//   - dev/BenchPage.js (solver test bench)
//
// Single source of truth: TDR + Brock + params → flight tiles ready for
// either display or /api/solve-routes. Keeping this in one place
// guarantees the bench tests the solver against the EXACT same input
// shape that production uses.

import {
  getConnectionCategory,
  isOvernightFlight,
  calculateConnectionTime,
} from './connectionUtils';

const FWD_BINS = ['A', 'B', 'C'];
const AFT_BINS = ['D', 'E', 'F'];

// ──────────────────────────────────────────────────────────────────────
// Pure helpers
// ──────────────────────────────────────────────────────────────────────

export const isHotOrPriority = (flight) => {
  const category = flight.connectionCategory;
  return category === 'hot' || category === 'priority';
};

export const getFwdAftBags = (flight) => {
  let fwdBags = 0;
  let aftBags = 0;
  if (flight.binDistribution && Object.keys(flight.binDistribution).length > 0) {
    Object.entries(flight.binDistribution).forEach(([bin, count]) => {
      if (FWD_BINS.includes(bin)) fwdBags += count || 0;
      else if (AFT_BINS.includes(bin)) aftBags += count || 0;
    });
  }
  if (fwdBags === 0 && aftBags === 0 && flight.bags > 0) {
    return { fwdBags: 0, aftBags: 0 };
  }
  return { fwdBags, aftBags };
};

export const shouldSplitFlight = (flight, splitMode) => {
  if (!splitMode) return false;
  const { fwdBags, aftBags } = getFwdAftBags(flight);
  return fwdBags > 0 && aftBags > 0;
};

export const shouldCreateSingleCompartmentFlight = (flight, splitMode) => {
  if (!splitMode) return false;
  const { fwdBags, aftBags } = getFwdAftBags(flight);
  return (fwdBags > 0 && aftBags === 0) || (fwdBags === 0 && aftBags > 0);
};

// TDR may report 0 bags for a flight that Brock then provides bin-level
// bag counts for. Treat any source of bag data as "has bags".
const flightHasBags = (flight) => {
  if ((flight.bags || 0) > 0) return true;
  if ((flight.brockBags || 0) > 0) return true;
  if (flight.binDistribution && Object.values(flight.binDistribution).some((c) => c > 0)) return true;
  return false;
};

export const shouldAutoSplitMixed = (flight) => {
  const hasBags = flightHasBags(flight);
  const hasFreightByBin = flight.freightByBin && Object.values(flight.freightByBin).some((c) => c > 0);
  const hasAllFreight = flight.allFreight && flight.allFreight.length > 0;
  const hasDestinationFreight = flight.destinations && flight.destinations.some((d) => d.freight && d.freight.length > 0);
  return hasBags && (hasFreightByBin || hasAllFreight || hasDestinationFreight);
};

export const isFreightOnlyFlight = (flight) => {
  const hasBags = flightHasBags(flight);
  const hasFreightByBin = flight.freightByBin && Object.values(flight.freightByBin).some((c) => c > 0);
  const hasAllFreight = flight.allFreight && flight.allFreight.length > 0;
  const hasDestinationFreight = flight.destinations && flight.destinations.some((d) => d.freight && d.freight.length > 0);
  return (hasFreightByBin || hasAllFreight || hasDestinationFreight) && !hasBags;
};

export const createMixedSplitFlights = (flight) => {
  const freightFlight = {
    ...flight,
    uniqueId: `${flight.uniqueId}-FREIGHT`,
    originalId: flight.uniqueId,
    isColdMixedSplit: true,
    isFreightOnly: true,
    bags: 0,
    // Zero out every bag-presence signal so flightHasBags() returns false on
    // re-processing — otherwise downstream split logic re-splits this tile.
    tdrBags: 0,
    brockBags: 0,
    binDistribution: {},
    destinations: flight.destinations ? flight.destinations.map((d) => ({ ...d, bags: 0 })) : [],
    connectionCategory: 'freight-only',
  };
  const bagFlight = {
    ...flight,
    uniqueId: `${flight.uniqueId}-BAGS`,
    originalId: flight.uniqueId,
    isColdMixedSplit: true,
    isBagsOnly: true,
    allFreight: [],
    freightByBin: {},
    destinations: flight.destinations ? flight.destinations.map((d) => ({ ...d, freight: [] })) : [],
  };
  return [freightFlight, bagFlight];
};

export const createSplitFlights = (flight) => {
  const { fwdBags, aftBags } = getFwdAftBags(flight);
  const fwdBinDistribution = {};
  const aftBinDistribution = {};
  const fwdFreightByBin = {};
  const aftFreightByBin = {};
  let fwdFreightCount = 0;
  let aftFreightCount = 0;

  if (flight.binDistribution) {
    Object.entries(flight.binDistribution).forEach(([bin, count]) => {
      if (FWD_BINS.includes(bin)) fwdBinDistribution[bin] = count;
      else if (AFT_BINS.includes(bin)) aftBinDistribution[bin] = count;
    });
  }
  if (flight.freightByBin) {
    Object.entries(flight.freightByBin).forEach(([bin, count]) => {
      if (FWD_BINS.includes(bin)) {
        fwdFreightByBin[bin] = count;
        fwdFreightCount += count || 0;
      } else if (AFT_BINS.includes(bin)) {
        aftFreightByBin[bin] = count;
        aftFreightCount += count || 0;
      }
    });
  }

  // TDR-derived allFreight items aren't bin-tagged, so we can't split them.
  // Attach the full list to whichever compartment carries more freight pieces
  // (ties go to FWD) so the dispatcher sees each item exactly once.
  const fwdGetsFreightItems = fwdFreightCount >= aftFreightCount;

  return [
    {
      ...flight,
      uniqueId: `${flight.uniqueId}-FWD`,
      originalId: flight.uniqueId,
      bags: fwdBags,
      isSplit: true,
      splitType: 'FWD',
      binDistribution: fwdBinDistribution,
      allFreight: fwdGetsFreightItems ? (flight.allFreight || []) : [],
      freightByBin: fwdFreightByBin,
    },
    {
      ...flight,
      uniqueId: `${flight.uniqueId}-AFT`,
      originalId: flight.uniqueId,
      bags: aftBags,
      isSplit: true,
      splitType: 'AFT',
      binDistribution: aftBinDistribution,
      allFreight: !fwdGetsFreightItems ? (flight.allFreight || []) : [],
      freightByBin: aftFreightByBin,
    },
  ];
};

export const createSingleCompartmentFlight = (flight) => {
  const { fwdBags, aftBags } = getFwdAftBags(flight);
  const isForward = fwdBags > 0;
  const compartmentType = isForward ? 'FWD' : 'AFT';
  const bagCount = isForward ? fwdBags : aftBags;
  const relevantBins = isForward ? FWD_BINS : AFT_BINS;

  const binDistribution = {};
  if (flight.binDistribution) {
    Object.entries(flight.binDistribution).forEach(([bin, count]) => {
      if (relevantBins.includes(bin)) binDistribution[bin] = count;
    });
  }

  const freightByBin = {};
  let freightCount = 0;
  if (flight.freightByBin) {
    Object.entries(flight.freightByBin).forEach(([bin, count]) => {
      if (relevantBins.includes(bin)) {
        freightByBin[bin] = count;
        freightCount += count || 0;
      }
    });
  }

  return {
    ...flight,
    uniqueId: `${flight.uniqueId}-${compartmentType}`,
    originalId: flight.uniqueId,
    bags: bagCount,
    isSplit: true,
    splitType: compartmentType,
    binDistribution,
    allFreight: freightCount > 0 ? (flight.allFreight || []) : [],
    freightByBin,
  };
};

const normalizeFlightNumber = (flightNum) => {
  if (!flightNum) return '';
  const wnMatch = flightNum.match(/WN\s*(\d+)/i);
  if (wnMatch && wnMatch[1]) return parseInt(wnMatch[1], 10).toString();
  const fiMatch = flightNum.match(/FI\s*0*(\d+)/i);
  if (fiMatch && fiMatch[1]) return `FI ${parseInt(fiMatch[1], 10)}`;
  const tkMatch = flightNum.match(/TK\s*0*(\d+)/i);
  if (tkMatch && tkMatch[1]) return `TK ${parseInt(tkMatch[1], 10)}`;
  return flightNum;
};

// ──────────────────────────────────────────────────────────────────────
// Main pipeline
// ──────────────────────────────────────────────────────────────────────

const mergeAndProcessData = (tdrData, brockData, inboundETA, inboundGate, params) => {
  const tdrOutbound = tdrData.outbound || [];
  const brockBins = brockData.bins || {};
  const flightMap = new Map();

  tdrOutbound.forEach((flight) => {
    const k = normalizeFlightNumber(flight.flightNumber);
    flightMap.set(k, {
      ...flight,
      flightNumber: k,
      tdrBags: flight.bags,
      brockBags: 0,
      binDistribution: {},
      freightByBin: {},
      isMultiDestination: flight.destinations && flight.destinations.length > 1,
      sch: null,
    });
  });

  Object.entries(brockBins).forEach(([binName, binData]) => {
    (binData.flights || []).forEach((brockFlight) => {
      const k = normalizeFlightNumber(brockFlight.flightNumber);
      if (!flightMap.has(k)) return;
      const existing = flightMap.get(k);
      const brockDest = brockFlight.dest;
      const isForThisDest = !existing.isMultiDestination ||
        (existing.destinations || []).some((d) => d.city === brockDest);
      if (!isForThisDest) return;
      existing.binDistribution[binName] = (existing.binDistribution[binName] || 0) + brockFlight.bags;
      existing.brockBags += brockFlight.bags;
      if (brockFlight.freight > 0) {
        existing.freightByBin[binName] = (existing.freightByBin[binName] || 0) + brockFlight.freight;
      }
      if (!existing.sch && brockFlight.sch) existing.sch = brockFlight.sch;
    });
  });

  const finalFlights = [];
  flightMap.forEach((flight, key) => {
    const isOvernight = isOvernightFlight(flight);
    const connectionTime = calculateConnectionTime(flight.etd, inboundETA, isOvernight);
    const isCancelled = flight.status?.toLowerCase().includes('cancel') || false;
    const category = isCancelled ? 'cancelled' : getConnectionCategory(
      connectionTime, params, isOvernight,
      { scheduledTime: flight.sch, inboundETA },
    );
    const wouldBeColdButForSchedule = connectionTime >= (params.coldThresholdHours * 60) &&
      category === 'normal' && flight.sch && flight.sch !== flight.etd;
    const allTdrFreight = (flight.destinations || []).length > 0
      ? (flight.destinations || []).flatMap((d) => d.freight || [])
      : (flight.freight || []);

    const inboundRegistry = tdrData?.inbound?.registry || '';
    const isThruBag = !!(inboundRegistry && flight.registration &&
      inboundRegistry.toUpperCase() === flight.registration.toUpperCase());
    const isSameGate = !!(inboundGate && flight.gate &&
      inboundGate.toUpperCase() === flight.gate.toUpperCase() &&
      category !== 'cold');

    let updatedNotes = flight.notes || '';
    if (isSameGate && !flight.isLocal) {
      updatedNotes = updatedNotes ? `**Stays at Gate** - ${updatedNotes}` : '**Stays at Gate**';
    }
    if (isThruBag && !flight.isLocal) {
      updatedNotes = updatedNotes ? `**THRU** - ${updatedNotes}` : '**THRU**';
    }
    // Bagged flight the Brock never scanned (backend stamps the verdict,
    // exclusions built in). A heads-up for the driver, seeded as a REAL
    // note: visible on the tile, editable through the pencil, printed
    // verbatim on the run sheet.
    if (flight.brockScanned === false) {
      const noScans = 'No Brock scans - these bags may not be on the aircraft';
      updatedNotes = updatedNotes ? `${updatedNotes} | ${noScans}` : noScans;
    }

    finalFlights.push({
      ...flight,
      destinations: flight.destinations,
      allFreight: allTdrFreight,
      uniqueId: `${key}-${flight.destination}-0`,
      originalId: `${key}-${flight.destination}`,
      connectionTime,
      connectionCategory: category,
      isOvernight,
      wouldBeColdButForSchedule,
      isThruBag,
      isSameGate,
      notes: updatedNotes,
    });
  });
  return finalFlights;
};

const sortFlights = (flights) => {
  return [...flights].sort((a, b) => {
    const aIsHot = isHotOrPriority(a);
    const bIsHot = isHotOrPriority(b);
    if (aIsHot && !bIsHot) return -1;
    if (!aIsHot && bIsHot) return 1;

    const aIsOvernight = a.isOvernight || false;
    const bIsOvernight = b.isOvernight || false;
    if (aIsOvernight && !bIsOvernight) return 1;
    if (!aIsOvernight && bIsOvernight) return -1;

    const parseETDForSort = (etd) => {
      if (!etd) return 0;
      const [hours, minutes] = etd.replace(/[LZ]/gi, '').split(':').map(n => Number(n) || 0);
      if (hours < 5) return (hours + 24) * 60 + minutes;
      return hours * 60 + minutes;
    };

    const aMinutes = parseETDForSort(a.etd);
    const bMinutes = parseETDForSort(b.etd);
    return aMinutes - bMinutes;
  });
};

const detectSameDestinations = (sortedFlights) => {
  const destinationFlightMap = new Map();
  sortedFlights.forEach((flight) => {
    const baseFlightNumber = flight.flightNumber?.replace(/-FWD|-AFT|-BAGS|-FREIGHT/g, '');
    if (flight.destination) {
      const destination = flight.destination.toUpperCase();
      if (!destinationFlightMap.has(destination)) destinationFlightMap.set(destination, []);
      const existingFlight = destinationFlightMap.get(destination).find(
        (f) => f.flightNumber?.replace(/-FWD|-AFT|-BAGS|-FREIGHT/g, '') === baseFlightNumber
      );
      if (!existingFlight) destinationFlightMap.get(destination).push(flight);
    }
  });

  sortedFlights.forEach((flight) => {
    if (flight.destination) {
      const destination = flight.destination.toUpperCase();
      const sameDestFlights = destinationFlightMap.get(destination) || [];
      const baseFlightNumber = flight.flightNumber?.replace(/-FWD|-AFT|-BAGS|-FREIGHT/g, '');
      const otherFlights = sameDestFlights.filter(
        (f) => f.flightNumber?.replace(/-FWD|-AFT|-BAGS|-FREIGHT/g, '') !== baseFlightNumber
      );
      if (otherFlights.length > 0) {
        flight.hasMultipleToSameDestination = true;
        flight.otherSameDestinationFlights = otherFlights.map((f) => ({
          flightNumber: f.flightNumber,
          etd: f.etd,
        }));
        // Seed the same-city warning as a REAL note on the tile — visible on
        // the board and editable through the tile's Edit modal like any other
        // note. The printed sheet takes notes verbatim, so what the
        // dispatcher leaves here is what the driver reads.
        const warning = `${sameDestFlights.length} ${destination} flights - read tag numbers please`;
        if (!flight.notes) {
          flight.notes = warning;
        } else if (!flight.notes.includes('read tag numbers please')) {
          flight.notes = `${flight.notes} | ${warning}`;
        }
      }
    }
  });
};

/**
 * Compute the unassigned flight tiles array. Pure function — same inputs
 * always produce the same output. Used by both the breakdown page (which
 * passes its current driver assignments to filter against) and the bench
 * (which always passes an empty assignedFlightIds).
 *
 * Output is exactly what /api/solve-routes expects in its `flights` field.
 */
export function computeUnassignedTiles({
  tdrData,
  brockData,
  params,
  inboundETA,
  inboundGate,
  splitMode = false,
  assignedFlightIds = new Set(),
}) {
  if (!tdrData || !brockData) return [];

  const processedFlights = mergeAndProcessData(tdrData, brockData, inboundETA, inboundGate, params);

  const unassignedFlights = processedFlights.filter((flight) =>
    !assignedFlightIds.has(flight.uniqueId) &&
    !assignedFlightIds.has(flight.originalId || flight.uniqueId)
  );

  const finalFlights = [];
  unassignedFlights.forEach((flight) => {
    if (flight.isColdMixedSplit && splitMode) {
      let processedFlight = { ...flight };
      if (flight.isBagsOnly && flight.binDistribution) {
        const fwdBags = (flight.binDistribution.A || 0) + (flight.binDistribution.B || 0) + (flight.binDistribution.C || 0);
        const aftBags = (flight.binDistribution.D || 0) + (flight.binDistribution.E || 0) + (flight.binDistribution.F || 0);
        if (fwdBags > 0 && aftBags === 0) processedFlight.splitType = 'FWD';
        else if (aftBags > 0 && fwdBags === 0) processedFlight.splitType = 'AFT';
      } else if (flight.isFreightOnly && flight.freightByBin) {
        const fwdFreight = (flight.freightByBin.A || 0) + (flight.freightByBin.B || 0) + (flight.freightByBin.C || 0);
        const aftFreight = (flight.freightByBin.D || 0) + (flight.freightByBin.E || 0) + (flight.freightByBin.F || 0);
        if (fwdFreight > 0 && aftFreight === 0) processedFlight.splitType = 'FWD';
        else if (aftFreight > 0 && fwdFreight === 0) processedFlight.splitType = 'AFT';
        // Freight spanning both compartments stays unlabeled.
      }
      finalFlights.push(processedFlight);
    } else if (shouldAutoSplitMixed(flight)) {
      const [freightFlight, bagFlight] = createMixedSplitFlights(flight);
      if (!assignedFlightIds.has(freightFlight.uniqueId)) finalFlights.push(freightFlight);
      // The bag half of a mixed flight may itself span FWD and AFT bins —
      // apply the same compartment splitting we'd do for a non-mixed flight.
      if (shouldSplitFlight(bagFlight, splitMode)) {
        const [bagFwd, bagAft] = createSplitFlights(bagFlight);
        if (!assignedFlightIds.has(bagFwd.uniqueId)) finalFlights.push(bagFwd);
        if (!assignedFlightIds.has(bagAft.uniqueId)) finalFlights.push(bagAft);
      } else if (shouldCreateSingleCompartmentFlight(bagFlight, splitMode)) {
        const singleComp = createSingleCompartmentFlight(bagFlight);
        if (!assignedFlightIds.has(singleComp.uniqueId)) finalFlights.push(singleComp);
      } else if (!assignedFlightIds.has(bagFlight.uniqueId)) {
        finalFlights.push(bagFlight);
      }
    } else if (shouldSplitFlight(flight, splitMode)) {
      const [fwdFlight, aftFlight] = createSplitFlights(flight);
      if (!assignedFlightIds.has(fwdFlight.uniqueId)) finalFlights.push(fwdFlight);
      if (!assignedFlightIds.has(aftFlight.uniqueId)) finalFlights.push(aftFlight);
    } else if (shouldCreateSingleCompartmentFlight(flight, splitMode)) {
      finalFlights.push(createSingleCompartmentFlight(flight));
    } else {
      let processedFlight = isFreightOnlyFlight(flight)
        ? { ...flight, connectionCategory: 'freight-only' }
        : flight;
      if (splitMode) {
        if (isFreightOnlyFlight(flight) && flight.freightByBin) {
          const fwdFreight = (flight.freightByBin.A || 0) + (flight.freightByBin.B || 0) + (flight.freightByBin.C || 0);
          const aftFreight = (flight.freightByBin.D || 0) + (flight.freightByBin.E || 0) + (flight.freightByBin.F || 0);
          if (fwdFreight > 0 && aftFreight === 0) {
            processedFlight = { ...processedFlight, splitType: 'FWD', isSplit: true };
          } else if (aftFreight > 0 && fwdFreight === 0) {
            processedFlight = { ...processedFlight, splitType: 'AFT', isSplit: true };
          }
          // Freight spanning both compartments stays unlabeled.
        } else if (flight.isColdMixedSplit && flight.isBagsOnly && flight.binDistribution) {
          const fwdBags = (flight.binDistribution.A || 0) + (flight.binDistribution.B || 0) + (flight.binDistribution.C || 0);
          const aftBags = (flight.binDistribution.D || 0) + (flight.binDistribution.E || 0) + (flight.binDistribution.F || 0);
          if (fwdBags > 0 && aftBags === 0) {
            processedFlight = { ...processedFlight, splitType: 'FWD', isSplit: true };
          } else if (aftBags > 0 && fwdBags === 0) {
            processedFlight = { ...processedFlight, splitType: 'AFT', isSplit: true };
          }
          // Bags spanning both compartments stays unlabeled.
        }
      }
      finalFlights.push(processedFlight);
    }
  });

  const sortedFlights = sortFlights(finalFlights);
  detectSameDestinations(sortedFlights);
  return sortedFlights;
}
